import { Injectable } from '@angular/core';
import { IgenomicsState } from '../../core/interfaces/genomics-state.interface';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import {
  ClearDataTablelistAction,
  ClearIdentifierDataTablelistAction,
  ClearIdsTablelistAction,
  ClearTablelistAction,
  FetchTablelistConfigurationTemplateAction,
  FetchTablelistPageDataAction,
  InitTablelistAction,
  SetEntitiesAmountAction,
  SetIdsTablelistAction,
  UpdateIdsTablelistAction,
} from '../store/tablelist.actions';
import {
  selectTablelistFeatureType,
  selectTablelistFeatureConfig,
  selectTablelistListFeature,
  selectTablelistFeatureAmount
} from '../store/tablelist.selector';
import {
  bufferCount,
  concatMap,
  filter,
  flatMap,
  map,
  retry,
  take,
  tap
} from 'rxjs/operators';
import {
    ITablelistActionConfig,
    ITablelistStateActionConfigTemplate,
    ITablelistStateListItem,
    ITablelistStateListItemConfig,
    ResourceTypes
} from '../interfaces';
import { TablelistService } from './tablelist.service';
import { DataHandlerHelperServices } from './data-handler.helper.services';
import { Subject } from 'rxjs';
import { LogService } from '@ispm/core/services/log.service';
import { SimpleSearchService } from './simple-search.service';

@Injectable()
export class TablelistStoreHandlerService extends DataHandlerHelperServices {
  private static readonly IDS_ONLY_KEY_SUFFIX = '_IDS_REQUESTS';

  public lastIdsCall = false;

  private fieldList = '';
  private idsSubject = new Subject();
  private fetchAdditionalData: boolean = false;
  private additionalResourcesToRetrieve: any[];
  private readonly  TABLE_LIST_STORE_HANDLER = 'TABLE_LIST_STORE_HANDLER';

  constructor(
    store: Store<IgenomicsState>,
    private tableListService: TablelistService,
    private logService: LogService,
    private simpleSearchService: SimpleSearchService
  ) {
    super(store);
  }

  public setFieldList(value) {
    this.fieldList = value;
  }

  public getTablelistTemplateSnapshot(type: string): Promise<ITablelistStateListItemConfig> {
    return this.store
      .select('tablelist', 'list')
      .map(templates => templates[type])
      .filter(data => !!data && data.config)
      .take(1)
      .map(data => data.config)
      .toPromise();
  }

  public init(resourceType: ResourceTypes, configTemplateType): Observable<any> {
    return this.tableListService.loadConfigTemplate(resourceType, configTemplateType).pipe(
      tap(() => {
        this.dispatch(new InitTablelistAction());
      })
    );
  }

  public clearAll(resourceType: ResourceTypes, configTemplateType) {
    this.dispatch(new ClearTablelistAction({
      configTemplateType: configTemplateType,
      resourceType
    }));
    this.dispatch(new ClearIdentifierDataTablelistAction({
      configTemplateType: configTemplateType,
      resourceType
    }));
  }

  public clear(resourceType: ResourceTypes, configTemplateType): Observable<any> {
    return this.store.select(selectTablelistListFeature).pipe(
      take(1),
      filter(list => list && list.hasOwnProperty(configTemplateType)),
      tap(() => {
        this.dispatch(new ClearTablelistAction({
          configTemplateType: configTemplateType,
          resourceType
        }));
      })
    );
  }

  public clearIds(resourceType: ResourceTypes, configTemplateType, clubbed = false): Observable<any> {
    return this.store.select(selectTablelistListFeature).pipe(
      take(1),
      filter(list => list && list.hasOwnProperty(configTemplateType)),
      tap(() => {
        this.dispatch(new ClearIdsTablelistAction({
          configTemplateType: configTemplateType,
          resourceType
        }));

        if (clubbed) {
          this.dispatch(new ClearDataTablelistAction({
            configTemplateType: configTemplateType,
            resourceType
          }));
        }
      })
    );
  }

  public getDataList(buffer, offset = 0, limit = 20) {
    return buffer.slice(offset, limit);
  }

  public loadConfigurationTemplate(request: ITablelistStateActionConfigTemplate) {
    const request$ = this.tableListService.loadConfigTemplate(request.resourceType, request.configTemplateType)
      .pipe(
        tap((config: ITablelistActionConfig) => {
          request.item = config;
          this.fetchAdditionalData = config['fetchAdditionalData'];

          if (config['additionalResourcesToRetrieve']) {
            this.additionalResourcesToRetrieve = config['additionalResourcesToRetrieve'];
          }

          this.dispatch(new FetchTablelistConfigurationTemplateAction(request));
        })
      ).publish();
    request$.connect();

    return request$;
  }

  public resolveConfigurationTemplate(request: ITablelistStateActionConfigTemplate) {
    const transactionId = request.transactionId ? request.transactionId : this.logService.generateTransactionId();
    this.logService.info(`[tablelist-store-handler.service] Getting Configuration Template`, this.TABLE_LIST_STORE_HANDLER, transactionId);

    return this.getConfigTemplate({ configTemplateType: request.configTemplateType})
      .pipe(
        tap(configTemplate => {
          if (!configTemplate) {
            this.tableListService.loadConfigTemplate(request.resourceType, request.configTemplateType)
              .subscribe((config: any) => {
                this.logService.info(
                  `[tablelist-store-handler.service] Loading Configuration Template Done`,
                  this.TABLE_LIST_STORE_HANDLER,
                  transactionId
                );
                request.item = config;

                if (config) {
                  this.fetchAdditionalData = config['fetchAdditionalData'] ? config['fetchAdditionalData'] : false;
                  this.dispatch(new FetchTablelistConfigurationTemplateAction(request));
                }
              });
          }
        }),
        filter(config => config != null),
        map(data => data.config),
        take(1)
      );
  }

  public requestNextPageIdsSimpleSearch(
    simpleSearch: any,
    searchText: string,
    configTemplateType: string,
    page: number,
    sortField: string,
    sortOrder: number,
    maxItemsPerPage: number,
    queryOrigin: string,
    associationItems: any[] = []
  ): Observable<any> {
    if (!!simpleSearch) {
      return this.simpleSearchService
        .search(simpleSearch, queryOrigin, searchText, page, sortField, sortOrder, null, false, null, associationItems)
        .pipe(
          map((data: any) => {
            if (this.simpleSearchService.requestError(data)) {
              data = [];
            } else { // Getting count only when it is the first page and if the total items count is less than maxItemsPerPage
              setTimeout(() => {
                this.simpleSearchService.search(simpleSearch, queryOrigin, searchText, null, null, null, null, true, null, associationItems)
                  .pipe(
                    take(1),
                    map((d: any) => {
                      if (Array.isArray(d) && d.length > 0) {
                        this.dispatch(new SetEntitiesAmountAction(configTemplateType, d[0].count));
                      }
                    })
                  )
                  .subscribe();
              }, 200);
            }

            return data;
          })
        );
    }
  }

  public requestNextPageIds(
    resourceType: ResourceTypes,
    configTemplateType,
    query,
    page,
    sortField,
    sortOrder,
    chunkSize,
    hasSearchMode,
    dynamicSearchQuery,
    sortByType,
    resource,
    sortPath: boolean,
    associationItems,
    adaptableFilters,
    isClubbed: boolean = false,
    patientIds?: string
  ): Observable<any> {
    return this.tableListService.getIds(
        resourceType,
        configTemplateType,
        query,
        page,
        sortField,
        sortOrder,
        chunkSize,
        hasSearchMode,
        dynamicSearchQuery,
        sortByType,
        resource,
        sortPath,
        associationItems,
        adaptableFilters,
        isClubbed,
        patientIds
      )
      .pipe(
        map((data: any) => {
          data.currentPage = page;

          if (page === 1) {
            this.dispatch(new SetIdsTablelistAction({
              ids: data.resources_ids,
              configTemplateType: configTemplateType,
              resourceType
            }));
          } else {
            this.dispatch(new UpdateIdsTablelistAction({
              ids: data.resources_ids,
              configTemplateType: configTemplateType,
              resourceType
            }));
          }

          if (isClubbed) {
            this.dispatch(new SetEntitiesAmountAction(configTemplateType, data.totalEntities));
            this.dispatch(new FetchTablelistPageDataAction({
              items: data.resources_data,
              configTemplateType: configTemplateType,
              resourceType,
              isClubbed
            }));
          }

          return data;
        })
      )
      .catch((err) => {
        console.log('error in fetching ids ', err);
        switch (err && err.code) {
          case -1:
            return Observable.of([]);
          default:
            return Observable.throw(err);
        }
      });
  }

  

  // public extractAllIdentifiers(workListRowList, configTemplateType){
  //   let aggIdentifierObject = this.getAggIdentifierMap(workListRowList)
  //   let aggIdentifierMap = aggIdentifierObject.map
  //   return this._ispmHttpClient.get(aggIdentifierObject.query).subscribe(responseAggIdentifiers =>{
  //       for(let patient in aggIdentifierMap){
  //           let value = aggIdentifierMap[patient]
  //           if ('otherKnownIds' in value){
  //             let aggIdentifierList = [value['aggIdentifier']]
  //             value.otherKnownIds.forEach(otherKnownId => {
  //                 for (let response of responseAggIdentifiers){
  //                     if (response.id == otherKnownId){
  //                       aggIdentifierList.push(response.aggIdentifier)
  //                     }
  //                 }
  //                 let aggregatedAggIdentifiers = this.aggregateAggIdentifiers(aggIdentifierList)
  //                 aggIdentifierMap[patient] = {aggIds: aggregatedAggIdentifiers}
  //             })
  //         }
  //       }
  //       this.dispatch(new SetIdentifierDataAction({
  //         configTemplate: configTemplateType,
  //         aggIds: aggIdentifierMap
  //       }))
  //       return aggIdentifierMap
  //     });
  //   }


  
  public requestPagesIds(
    resourceType: ResourceTypes,
    configTemplateType,
    query,
    startPage,
    sortField,
    sortOrder,
    chunkSize,
    hasSearchMode,
    dynamicSearchQuery,
    sortByType,
    resource,
    offset,
    sortPath,
    associationItems,
    adaptableFilters,
    isClubbed: boolean = false
  ): Observable<any> {
    return this.getAllIds(resourceType, configTemplateType, query, startPage, sortField, sortOrder, chunkSize, hasSearchMode, dynamicSearchQuery, sortByType, resource, offset, sortPath, associationItems, adaptableFilters, isClubbed)
      .pipe(
        bufferCount(1),
        flatMap(data => data)// array to stream
      );
  }

  public getQuery(
    resourceType: ResourceTypes,
    configTemplateType,
    page,
    sortField,
    sortOrder,
    chunkSize,
    dynamicSearchQuery,
    sortByType,
    resource,
    sortPath: boolean,
    associationItems,
    adaptableFilters,
    patientIds?: string
  ): Observable<any> {
    return this.tableListService.getQuery(resourceType, configTemplateType, page, sortField, sortOrder, chunkSize, dynamicSearchQuery, sortByType, resource, sortPath, associationItems, adaptableFilters, patientIds);
  }

  private getAllIds(
    resourceType,
    configTemplateType,
    query,
    startPage,
    sortField,
    sortOrder,
    chunkSize,
    hasSearchMode,
    dynamicSearchQuery,
    sortByType,
    resource,
    offset,
    sortPath,
    associationItems,
    adaptableFilters,
    isClubbed: boolean = false
  ) {
    if (!(isClubbed)) {
      this.clearPagesIdRequests(configTemplateType);
    }

    this.getIdsPerPage(resourceType, configTemplateType, query, startPage, sortField, sortOrder, chunkSize, hasSearchMode, dynamicSearchQuery, sortByType, resource, offset, sortPath, associationItems, adaptableFilters, isClubbed);

    return this.idsSubject.asObservable();
  }

  public clearPagesIdRequests(type: string = null) {
    this.idsSubject.complete();
    this.idsSubject = new Subject();
    const regex = type
        ? new RegExp(this.buildCacheKey(type, TablelistStoreHandlerService.IDS_ONLY_KEY_SUFFIX))
        : null;
    this.cancelPreviousRequests(regex);
  }

  private getIdsPerPage(
    resourceType,
    configTemplateType,
    query,
    page,
    sortField,
    sortOrder,
    chunkSize,
    hasSearchMode,
    dynamicSearchQuery,
    sortByType,
    resource,
    offset,
    sortPath,
    associationItems,
    adaptableFilters,
    isClubbed: boolean = false
  ) {
    const cachedKey = this.buildCacheKey(configTemplateType, TablelistStoreHandlerService.IDS_ONLY_KEY_SUFFIX, page);

    if (page === 1) {
      this.lastIdsCall = false;
    }

    const ongoingRequest = this.requestNextPageIds(resourceType, configTemplateType, query, page, sortField, sortOrder, chunkSize, hasSearchMode, dynamicSearchQuery, sortByType, resource, sortPath, associationItems, adaptableFilters, isClubbed).pipe(
      retry(2),
      map((data: any) => {
        return { ids: data.resources_ids, data: data.resources_data, currentPage: page};
      }),
      concatMap((data: any)  => {
        this.idsSubject.next(data);

        if (isClubbed) {
          return Observable.empty();
        }

        if (data.ids.length !== chunkSize) {
          this.lastIdsCall = true;
          return  Observable.empty();
        }

        return this.getIdsPerPage(resourceType, configTemplateType, query, ++page, sortField, sortOrder, chunkSize, hasSearchMode, dynamicSearchQuery, sortByType, resource, offset, sortPath, associationItems, isClubbed);
      })
    ).publish();

    this.cacheOngoingRequest(cachedKey, ongoingRequest, ongoingRequest.connect(), query);

    return ongoingRequest;
  }

  public requestPatientTests(configTemplateType, patient_id, org_id) {
    return this.tableListService.getResourceIdsPatientTests(ResourceTypes.Generic, configTemplateType, patient_id, org_id);
  }

  /**
   * @deprecated
   * @param configTemplateType
   * @param patient_id
   * @param revision
   */
  public getTreatments(configTemplateType, patient_id, revision) {
    return this.tableListService.getTreatments(ResourceTypes.Generic, configTemplateType, patient_id, revision);
  }

  public getSelectedTreatment(configTemplateType, revision) {
    return this.tableListService.getSelectedTreatment(ResourceTypes.Generic, configTemplateType, revision);
  }

  public requestPageData(
    resourceType: ResourceTypes,
    configTemplateType,
    resources_ids,
    dynamicSearchQuery,
    resource,
    associationItems,
    adaptableFilters
  ) {
    return this.tableListService.getData(resourceType, configTemplateType, this.fieldList, resources_ids, dynamicSearchQuery, resource, associationItems, adaptableFilters, this.fetchAdditionalData, this.additionalResourcesToRetrieve).pipe(
      tap((data: any) => {
        this.dispatch(new FetchTablelistPageDataAction({
          items: data.items,
          configTemplateType: configTemplateType,
          resourceType
        }));
      })
    );
  }

  public getConfigTemplate(configTemplateType: { configTemplateType: string}): Observable<ITablelistStateListItem> {
    return this.store.pipe(select(selectTablelistFeatureType(configTemplateType)));
  }

  public getResourceRecordsAmount(configTemplateType): Observable<number> {
    return this.store.pipe(select(selectTablelistFeatureAmount(configTemplateType)));
  }

  public getConfigTemplateItem(resource: string): Observable<ITablelistStateListItemConfig> {
    return this.store.pipe(
      map(selectTablelistFeatureConfig(resource)),
      filter(config => !!config)
    );
  }

  public requestTotalIds(resourceType: ResourceTypes, configTemplateType, dynamicSearchQuery, resource, sortPath, association): Observable<any> {
    // TODO: Export to EFFECT
    return this.tableListService.getTotalEntities(resourceType, configTemplateType, dynamicSearchQuery, resource, association).pipe(
      tap((data: { totalEntities: number}) => {
        this.dispatch(new SetEntitiesAmountAction(configTemplateType, data.totalEntities));
      })
    );
  }

  /**
   * @deprecated
   * @param configTemplateType
   */
  public getData(configTemplateType): Observable<Array<any>> {
    return this.store.select(selectTablelistListFeature).pipe(
      filter(list => list.hasOwnProperty(configTemplateType)),
      map((item) => item[configTemplateType].data)
    );
  }

  public addWorklistTempleteToStore(request) {
    this.dispatch(new FetchTablelistConfigurationTemplateAction(request));
  }
}

